import { DATE_TIME_FORMAT } from '@/libs/core/constants'
import fieldToSortStr from '@/libs/utils/fieldToSortStr'
import Table from '@/shared/Table'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import get from 'lodash/get'
import round from 'lodash/round'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import BarChart from './BarChart'
import { alpha } from '@mui/material/styles'
import Edit from '@mui/icons-material/Edit'
import Close from '@mui/icons-material/Close'
import Check from '@mui/icons-material/Check'
import FormDateTimePicker from '@/shared/FormCreator/FormItem/FormDateTimePicker'
import Spinner from '@/shared/Spinner'
import Tooltip from '@/shared/Tooltip'
import SmsTemplatePreviewDialog from './SmsTemplatePreviewDialog'
import Button from '@/shared/Button'
import usePermissions from '@/hooks/usePermissions'
import FormCreator from '@/shared/FormCreator'
import durationToTranslatedStr from '@/libs/utils/durationToTranslatedStr'
import Watermark from './Watermark/Watermark'
import generateUsername from './Watermark/GenerateUsername'

const mapStateToProps = ({ phishCampaign, auth, loading }) => ({
  account: auth.account,
  phishSmsCampaignDetails: phishCampaign.phishSmsCampaignDetails,
  isUsersLoading: loading['phishCampaign/getPhishSmsCampaignUsers'],
  isUpdating: loading['phishCampaign/updatePhishCampaign'],
})

const fieldMap = {
  userName: 'USER_NAME',
  email: 'EMAIL',
  tempName: 'TEMP_NAME',
  status: 'STATUS',
  sendingTime: 'SENDING_TIME',
  firstTimePhish: 'FRIST_PHISH_TIME',
}

const TextItem = ({ label, labelProps, value, valueProps, suffix, suffixProps }) => (
  <Grid container item alignItems="flex-start" spacing={1}>
    <Grid item {...labelProps}>{`${label}:`}</Grid>
    <Grid item {...valueProps}>
      <Stack direction="row" justifyContent="space-between">
        {value}

        {suffix && (
          <Grid item {...suffixProps}>
            {suffix}
          </Grid>
        )}
      </Stack>
    </Grid>
  </Grid>
)

const CardItem = ({ title, titleAction, subtitle, cardContent, children }) => (
  <Card
    sx={{
      mt: 2,
      background: theme => alpha(theme.palette.grey_80.main, 0.2),
      color: theme => theme.palette.secondary[80].main,
    }}
  >
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography fontWeight="bold">{title}</Typography>
        <Box sx={{ color: alpha('#000', 0.25), fontSize: 12 }}>{subtitle}</Box>
        {titleAction}
      </Stack>
      <Box padding={1}>{cardContent}</Box>
    </CardContent>
    {children}
  </Card>
)

const UserCountItem = ({ label, value, tooltip }) => (
  <TextItem
    label={label}
    value={value}
    labelProps={{ flex: 1 }}
    valueProps={{ sx: { flex: 2, borderBottom: '2px solid #00000040' } }}
    suffix={
      <Tooltip title={tooltip} placement="right-end">
        <HelpOutlineOutlinedIcon sx={{ color: alpha('#000', 0.25) }} />
      </Tooltip>
    }
  />
)

const PhishCampaignSmsDetails = ({
  dispatch,
  phishSmsCampaignDetails,
  fetchCampaignDetails,
  account,
  isUsersLoading,
  isUpdating,
}) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [sort, setSort] = useState([{ field: 'userName', sort: 'asc' }])
  const [searchUser, setSearchUser] = useState()
  const [searchStatus, setSearchStatus] = useState('ALL')

  const [isEditEndDate, setIsEditEndDate] = useState(false)
  const [endDate, setEndDate] = useState()

  const [previewTemplateId, setPreviewTemplateId] = useState()

  const companyId = useSelector(state => state.auth.account?.companyId)

  const accountEmail = useSelector(state => state.auth.account?.accountEmail)

  const employeeId = useSelector(state => state.auth.account?.employeeId)

  const username = generateUsername(employeeId, accountEmail)

  const canModify = usePermissions('smsCampaign_modify')

  const { accountId } = account || {}

  const {
    campaignMainName,
    campaignMainId,
    scheduledStartTimeS,
    scheduledEndTimeS,
    scheduledStartTimeE,
    scheduledEndTimeE,
    createTime,
    lastUpdateTime,
    campaignContent,
    status,
    runType,
    campaignCount,
    users,
  } = phishSmsCampaignDetails || {}

  const dayStr = t('general.day')
  const hourStr = t('general.hour')
  const minuteStr = t('general.minute')

  const campaignDuration = moment.duration(moment(scheduledEndTimeE).diff(scheduledStartTimeS))

  const campaignDurationStr = durationToTranslatedStr(campaignDuration, {
    day: dayStr,
    hour: hourStr,
    minute: minuteStr,
  })

  const runTypeStr = str => {
    switch (str) {
      case 'main':
        return t('general.run_type_main')
      case 'pre':
        return t('general.run_type_pre')
      case 'test':
        return t('general.run_type_test')
      default:
        return ''
    }
  }

  const remainDuration = moment.duration(moment(scheduledEndTimeE).diff())

  const remainDurationStr = durationToTranslatedStr(
    moment.duration(
      Math.max(Math.min(campaignDuration.asSeconds(), remainDuration.asSeconds()), 0),
      'seconds',
    ),
    {
      day: dayStr,
      hour: hourStr,
      minute: minuteStr,
    },
  )

  const {
    totalSend = 0,
    sucSend = 0,
    wait: waitSMS = 0,
    return: returnSMS = 0,
    linkedClicked = 0,
    dataEntered = 0,
  } = campaignCount || {}

  const { paging: usersPagination, data: usersData = [] } = users || {}

  const pagination = {
    ...usersPagination,
    page,
    pageSize,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
  }

  const cols = [
    { field: 'userName', headerName: t('general.name'), width: 200 },
    {
      field: 'email',
      headerName: t('general.email'),
      width: 200,
      renderCell: ({ value }) => (
        <Tooltip title={value} placement="top-end">
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'phone',
      headerName: `${t('general.phone')}`,
      width: 120,
    },
    {
      field: 'dept',
      headerName: `${t('general.department')}`,
      width: 120,
    },
    {
      field: 'tempName',
      headerName: `${t('general.template_name')}`,
      width: 120,
    },
    {
      field: 'userAction',
      headerName: `${t('general.user_behaviour')}`,
      width: 120,
    },
    {
      field: 'status',
      headerName: t('general.status'),
      width: 100,
      valueFormatter: ({ value }) => t(`status.${value}`),
    },
    {
      field: 'sendingTime',
      headerName: t('general.sent_time'),
      width: 200,
      valueFormatter: ({ value }) => value && moment(value).format(DATE_TIME_FORMAT),
    },
    {
      field: 'firstTimePhish',
      headerName: t('general.first_phished_time'),
      width: 200,
      valueFormatter: ({ value }) => value && moment(value).format(DATE_TIME_FORMAT),
    },
    {
      field: 'firstAction',
      headerName: `${t('general.first_action')}`,
      width: 120,
    },
  ]

  const chartsData = [
    { name: t('general.send_success'), value: sucSend, fill: '#FF9B0B' },
    { name: t('general.wait_mail'), value: waitSMS, fill: '#A4D7CD' },
    {
      name: t('general.return_mail'),
      value: returnSMS,
      fill: '#D6A4D7',
    },
  ]

  const behaviourChartsData = [
    { name: t('general.click_link'), value: linkedClicked || 0, fill: '#A4D7CD' },
    {
      name: t('general.enter_data'),
      value: dataEntered || 0,
      fill: '#3D85F0',
    },
  ]

  const generateLegendsText = value => {
    const percent = round((value / (totalSend || 1)) * 100)

    return `${percent}% (${value}/${totalSend})`
  }

  const submitEndDate = () => {
    dispatch({
      type: 'phishCampaign/updatePhishCampaign',
      payload: { campaignEndTime: endDate.toDate(), campaignId: campaignMainId },
      successCallback: () => {
        setIsEditEndDate(false)

        fetchCampaignDetails?.()

        dispatch({
          type: 'app/notify',
          payload: {
            message: t('messages.end_date_changed'),
            severity: 'success',
          },
        })
      },
    })
  }

  useEffect(() => {
    if (campaignMainId && accountId) {
      const statusValue = searchStatus === 'ALL' ? '' : searchStatus
      dispatch({
        type: 'phishCampaign/getPhishSmsCampaignUsers',
        payload: {
          campaignId: campaignMainId,
          params: {
            searchParam: searchUser,
            status: statusValue,
            campaignId: campaignMainId,
            accountId,
            pageIndex: page,
            pageSize,
            sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
          },
        },
      })
    }
  }, [dispatch, campaignMainId, accountId, page, pageSize, sort, searchUser, searchStatus])

  return (
    <Container
      sx={{
        color: theme => theme.palette.secondary[80].main,
      }}
    >
      <Typography variant="h5">{campaignMainName}</Typography>
      <CardItem
        title={t('general.campaign_info')}
        subtitle={`${t('general.create_time')}: ${moment(createTime).format(DATE_TIME_FORMAT)} ${t(
          'general.update_time',
        )}: ${
          lastUpdateTime
            ? moment(lastUpdateTime).format(DATE_TIME_FORMAT)
            : moment(createTime).format(DATE_TIME_FORMAT)
        }`}
        cardContent={
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Grid container item spacing={1}>
                <TextItem
                  label={t('general.start')}
                  value={
                    moment(scheduledStartTimeS).format(DATE_TIME_FORMAT) +
                    (scheduledStartTimeE
                      ? ` ~ ${moment(scheduledStartTimeE).format(DATE_TIME_FORMAT)}`
                      : '')
                  }
                />
                <TextItem
                  label={t('general.end')}
                  value={
                    isEditEndDate ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <FormDateTimePicker
                          formProps={{
                            field: {
                              value: endDate,
                              onChange: val => {
                                const newDate = moment(val)

                                setEndDate(newDate)
                              },
                            },
                          }}
                          itemProps={{
                            minDateTime: moment.max(
                              moment(),
                              moment(scheduledStartTimeS).add(1, 'days'),
                            ),
                          }}
                          inputConfig={{ disabled: isUpdating, sx: { width: 250 } }}
                        />
                        {isUpdating ? (
                          <Spinner />
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              color="secondary_80"
                              onClick={() => {
                                setIsEditEndDate(false)
                              }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="secondary_80"
                              onClick={() => {
                                submitEndDate()
                              }}
                            >
                              <Check fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography>
                          {' '}
                          {(scheduledEndTimeS
                            ? `${moment(scheduledEndTimeS).format(DATE_TIME_FORMAT)} ~ `
                            : '') + moment(scheduledEndTimeE).format(DATE_TIME_FORMAT)}
                        </Typography>
                        {campaignContent?.length === 1 &&
                          canModify &&
                          status !== 'COM' &&
                          moment(scheduledEndTimeE).isAfter(moment()) && (
                            <IconButton
                              size="small"
                              color="secondary_80"
                              onClick={() => {
                                setEndDate(scheduledEndTimeE)
                                setIsEditEndDate(true)
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          )}
                      </Stack>
                    )
                  }
                />
                <TextItem label={t('general.campaign_status')} value={t(`status.${status}`)} />
              </Grid>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Grid container item spacing={1}>
                <TextItem label={t('general.remaining_days')} value={remainDurationStr} />

                <TextItem label={t('general.campaign_days')} value={campaignDurationStr} />

                <TextItem label={t('general.campaign_run_type')} value={runTypeStr(runType)} />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <TextItem
                label={`${t('general.group_head_count')}, ${t('general.campaign_content')}`}
                value={
                  <Stack>
                    {campaignContent?.map((c, index) => {
                      const { smsTempId, people, content, group } = c
                      return (
                        <Stack
                          direction={'row'}
                          key={`content_${index + 1}`}
                          alignItems={'center'}
                          spacing={1}
                        >
                          <Typography>
                            {group} / {t('general.head_count', { num: people })}
                          </Typography>
                          {smsTempId ? (
                            <Button
                              variant="text"
                              sx={{ p: 0 }}
                              onClick={() => {
                                setPreviewTemplateId(smsTempId)
                              }}
                            >
                              {content}
                            </Button>
                          ) : (
                            <Typography>{content}</Typography>
                          )}
                        </Stack>
                      )
                    })}
                  </Stack>
                }
              />
            </Grid>
          </Grid>
        }
      />

      <CardItem
        title={t('general.total_summary')}
        cardContent={
          <Grid container spacing={4}>
            <Grid item md={6}>
              <Stack spacing={4}>
                <Stack spacing={1} alignItems="center">
                  <Typography textAlign="center">{t('general.delivery_data')}</Typography>
                  <UserCountItem
                    label={t('general.send_success')}
                    value={generateLegendsText(sucSend)}
                    tooltip={t('tooltips.send_success_users')}
                  />
                  <UserCountItem
                    label={t('general.wait_sms')}
                    value={generateLegendsText(waitSMS)}
                    tooltip={t('tooltips.wait_sms_users')}
                  />
                  <UserCountItem
                    label={t('general.return_mail')}
                    value={generateLegendsText(returnSMS)}
                    tooltip={t('tooltips.return_mail_users')}
                  />
                </Stack>

                <BarChart dataKey="value" data={chartsData} maxValue={totalSend} />
              </Stack>
            </Grid>

            <Grid item md={6}>
              <Stack spacing={4} sx={{ height: '100%' }}>
                <Stack spacing={1} alignItems="center">
                  <Typography textAlign="center">{t('general.user_behaviour')}</Typography>

                  <UserCountItem
                    label={t('general.click_link')}
                    value={generateLegendsText(linkedClicked || 0)}
                    tooltip={t('tooltips.click_link_users')}
                  />
                  <UserCountItem
                    label={t('general.enter_data')}
                    value={generateLegendsText(dataEntered || 0)}
                    tooltip={t('tooltips.enter_data_users')}
                  />
                </Stack>
                <Box sx={{ flex: 1 }} />
                <BarChart dataKey="value" data={behaviourChartsData} maxValue={totalSend} />
              </Stack>
            </Grid>
          </Grid>
        }
      />

      <CardItem
        title={t('general.send_success_list')}
        titleAction={
          <FormCreator
            formItems={[
              {
                name: 'search',
                defaultValue: '',
                inputConfig: {
                  placeholder: t('general.insert_keyword'),
                  onSubmit: value => {
                    setSearchUser(value)
                  },
                },
              },
              {
                label: t('general.status_selection'),
                name: 'status',
                defaultValue: 'ALL',
                type: 'select',
                inputConfig: {
                  onChange: value => {
                    const val = value.target?.value || value
                    setSearchStatus(val)
                  },
                  options: [
                    { value: 'ALL', label: t('status.ALL') },
                    { value: 'TBS', label: t('status.TBS') },
                    { value: 'PRO', label: t('status.PRO') },
                    { value: 'SUC', label: t('status.SUC') },
                    { value: 'FAIL', label: t('status.FAIL') },
                  ],
                },
              },
            ]}
            flexDirection="row"
            submitComponent={() => null}
            resetComponent={() => null}
          />
        }
      >
        <div style={{ position: 'relative' }}>
          <Table
            minHeight={300}
            columns={cols}
            rows={usersData}
            dataGridConfig={{
              sortingMode: 'server',
              sortingOrder: ['desc', 'asc', null],
              sortModel: sort,
              onSortModelChange: newSort => setSort(newSort),
              borderColor: theme => theme.palette.other.black.main,
              getRowId: row => row.queueId,
              loading: isUsersLoading,
              isRowSelectable: params => params.row.status !== 'COM',
            }}
            pagination={pagination}
          />
          {companyId === 1 && <Watermark username={username} />}
        </div>
      </CardItem>

      <SmsTemplatePreviewDialog
        history
        open={!!previewTemplateId}
        onClose={() => {
          setPreviewTemplateId()
        }}
        smsTempId={previewTemplateId}
      />
    </Container>
  )
}

export default connect(mapStateToProps)(PhishCampaignSmsDetails)
